import styled, { keyframes } from 'styled-components'
import numberCardIcon from '../../images/UI_NumberCard_512x512.png'
import sequenceBackground from '../../images/UI_BGFormation.png'

const SHADOW_CARD = '0px 0px 4px 0px rgba(43,123,199,0.25)'
const CARD_CLIP   = 'polygon(0% 8%, 8% 0%, 100% 0%, 100% 92%, 92% 100%, 0% 100%)'
/* Fixed-size 45deg cut, so wide and tall blocks get the same angle */
const blockClip = (cut) => `polygon(0 ${cut}, ${cut} 0, 100% 0, 100% calc(100% - ${cut}), calc(100% - ${cut}) 100%, 0 100%)`
const moduleGridEnter = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
`

/* ── Page shell ─────────────────────────────────────────────── */

export const Bg = styled.div`
  min-height: 100vh;
  width: 100%;
`

export const MainLayout = styled.div`
  float: right;
  width: 80%;
  background: white;
  min-height: 100vh;
`

export const ContentLayout = styled.div`
  padding-top: 0;
  overflow-y: auto;
  min-height: 100vh;
  padding-bottom: 3rem;
`

/* ── Page header (same style as Magasin) ────────────────────── */

export const DetailHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1.25rem;
  padding: 1.25rem 2.5rem 0.75rem;
`

export const DetailIconBox = styled.div`
  width: 65px;
  height: 65px;
  border-radius: 15px;
  background: linear-gradient(223deg, rgba(0,200,248,1) 16%, rgba(104,94,252,1) 78%);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`

export const DetailIconImg = styled.img`
  width: 33px;
  height: 33px;
  object-fit: contain;
`

export const DetailTitle = styled.h1`
  font-family: Poppins;
  font-size: 2rem;
  font-weight: 600;
  color: #000000;
  margin: 0;
  line-height: 1.15;
`

export const DetailSubtitle = styled.p`
  font-family: Poppins;
  font-size: 1rem;
  font-weight: 400;
  color: #000000;
  margin: 0;
  margin-top:0.5rem;
  opacity: 0.55;
`

/* ── Breadcrumb ─────────────────────────────────────────────── */

export const BreadcrumbBar = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0 2.5rem 1rem;
`

export const BreadcrumbItem = styled.span`
  font-family: Poppins;
  font-size: 0.875rem;
  font-weight: ${props => props.active ? 500 : 300};
  color: ${props => props.active ? '#000000' : '#8D8D8D'};
  cursor: ${props => props.clickable ? 'pointer' : 'default'};
  transition: color 0.15s;
  &:hover { color: ${props => props.clickable ? '#3494FA' : 'inherit'}; }
`

export const BreadcrumbSep = styled.span`
  color: #8D8D8D;
  font-weight: 300;
  font-size: 0.875rem;
`

/* ── Two-column layout ──────────────────────────────────────── */

export const MainColumns = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1.5rem;
  padding: 0 2.5rem 2rem;
`

/* Header, breadcrumb, thumbnails and spacing take about this much of the viewport height */
const ABOVE_FOLD_CHROME = '280px'

/* Image width capped so the image + thumbnails fit in the viewport height;
   the purchase card takes the remaining width */
export const LeftCol = styled.div`
  flex: 0 1 calc((100vh - ${ABOVE_FOLD_CHROME}) * 770 / 479);
  min-width: 0;
`

export const ImgWrapper = styled.div`
  width: 100%;
  aspect-ratio: 770 / 479;
  clip-path: ${blockClip('80px')};
  overflow: hidden;
  margin-bottom: 0.6rem;
`

export const MainImg = styled.div`
  width: 100%;
  height: 100%;
  background-image: url(${props => props.src});
  background-size: contain;
  background-position: center;
  background-repeat: no-repeat;
`

export const ThumbStrip = styled.div`
  display: flex;
  gap: 0.55rem;
  align-items: center;
`

export const ThumbItem = styled.div`
  width: 120px;
  flex-shrink: 0;
  height: 60px;
  border-radius: 5px;
  background-image: url(${props => props.src});
  background-size: contain;
  background-position: center;
  background-repeat: no-repeat;
  cursor: pointer;
  opacity: ${props => props.active ? 1 : 0.55};
  outline: 2px solid ${props => props.active ? '#3494FA' : 'transparent'};
  outline-offset: 2px;
  transition: opacity 0.15s, outline-color 0.15s;
  &:hover { opacity: 1; }
`

/* ── Right column purchase card ─────────────────────────────── */

export const RightColWrapper = styled.div`
  flex: 1 0 450px;
  filter: drop-shadow(0px 0px 5.52px rgba(43,123,199,0.25));
`

export const RightCard = styled.div`
  background: white;
  clip-path: ${blockClip('36px')};
  padding: 1.75rem 2rem 1.75rem 1.75rem;
  display: flex;
  flex-direction: column;
`

export const TitleRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 0.6rem;
`

export const TitleCol = styled.div`
  flex: 1;
  min-width: 0;
`

export const CardDetailIconImg = styled.img`
  width: 58px;
  height: 58px;
  object-fit: contain;
  flex-shrink: 0;
`

export const CatLabel = styled.span`
  font-family: Poppins;
  font-size: 0.625rem;
  font-weight: 700;
  color: ${props => props.color || '#6462FC'};
  text-transform: uppercase;
  letter-spacing: 0.1em;
  display: block;
  margin-bottom: 0.3rem;
`

export const FormTitle = styled.h2`
  font-family: Poppins;
  font-size: 1.5rem;
  font-weight: 700;
  color: ${props => props.color || '#6462FC'};
  margin: 0;
  line-height: 1.15;
`

export const SepLine = styled.div`
  width: 100%;
  height: 1px;
  background: #E2E2E2;
  margin: 0.6rem 0;
`

export const PriceBlock = styled.div`
  margin: 0.3rem 0 0.5rem;
`

export const BigPrice = styled.div`
  font-family: Poppins;
  font-size: 2rem;
  font-weight: 700;
  color: #000000;
  line-height: 1.1;
  margin-bottom: 0.15rem;
`

export const TaxNote = styled.div`
  font-family: Poppins;
  font-size: 0.75rem;
  font-weight: 400;
  color: #BABABA;
`

export const GroupPriceNote = styled.div`
  font-family: Poppins;
  font-size: 0.8rem;
  font-weight: 500;
  color: #6B7280;
  margin-top: 0.3rem;
`

export const MetaRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.25rem 0;
  margin-top:0.5rem;
`

export const MetaLabel = styled.span`
  font-family: Poppins;
  font-size: 0.875rem;
  font-weight: 400;
  color: #000000;
  display: flex;
  align-items: center;
  gap: 0.45rem;
  & img {
    width: 11px;
    height: 11px;
    object-fit: contain;
    opacity: 0.55;
  }
`

export const MetaValue = styled.span`
  font-family: Poppins;
  font-size: 0.875rem;
  font-weight: 600;
  color: ${props => props.color || '#000000'};
`

export const QtySection = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 0.4rem 0;
`

export const QtyLabel = styled.span`
  font-family: Poppins;
  font-size: 0.875rem;
  font-weight: 400;
  color: #000000;
`

export const QtyCtrl = styled.div`
  display: flex;
  align-items: center;
  border: 1px solid #F1F1F1;
  border-radius: 7px;
  overflow: hidden;
`

export const QtyBtn = styled.button`
  width: 29px;
  height: 30px;
  background: #F2F2F2;
  border: none;
  font-family: Poppins;
  font-size: 1rem;
  font-weight: 600;
  color: #000000;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s;
  &:hover:not(:disabled) { background: #E5E5E5; }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
`

export const QtyNum = styled.span`
  min-width: 32px;
  text-align: center;
  font-family: Poppins;
  font-size: 0.875rem;
  font-weight: 600;
  color: #000000;
`

export const AddCartBtn = styled.button`
  width: 100%;
  height: 43px;
  background: ${props => props.disabled ? '#C5CCFF' : '#8EA2FF'};
  border: 1px solid #4465FE;
  border-radius: 7px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  font-family: Poppins;
  font-size: 1rem;
  font-weight: 600;
  color: white;
  cursor: ${props => props.disabled ? 'default' : 'pointer'};
  margin: 0.5rem 0;
  transition: background 0.15s;
  & img { width: 22px; height: 22px; object-fit: contain; }
  &:not(:disabled):hover { background: #7B91FF; }
`

export const SuccessBar = styled.div`
  width: 367px;
  min-height: 27px;
  background: #DCFFF1;
  border: 1px solid #44FEB4;
  border-radius: 7px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.3rem 0.75rem;
  cursor: pointer;
  margin-bottom: 0.5rem;
`

export const SuccessLeft = styled.span`
  font-family: Poppins;
  font-size: 0.8125rem;
  font-weight: 500;
  color: #2AD28F;
  display: flex;
  align-items: center;
  gap: 0.3rem;
  margin-left:0.5rem;
`

export const SuccessRight = styled.span`
  font-family: Poppins;
  font-size: 0.8125rem;
  font-weight: 500;
  color: #2AD28F;
`

export const TrustTxt = styled.p`
  font-family: Poppins;
  font-size: 0.8125rem;
  font-weight: 400;
  color: #BABABA;
  line-height: 1.75;
  margin: 0.5rem 0 0;
  white-space: pre-line;
`

/* ── Content sections ───────────────────────────────────────── */

export const ContentArea = styled.div`
  padding: 0 2.5rem 3rem;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
`

export const ContentBox = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: ${SHADOW_CARD};
  padding: 1.5rem 2rem;
`

export const SideBySide = styled.div`
  display: flex;
  align-items: stretch;
  gap: 1.25rem;

  & > * {
    min-width: 0;
  }

  @media (max-width: 900px) {
    flex-direction: column;
  }
`

export const BoxTitle = styled.h3`
  font-family: Poppins;
  font-size: 0.875rem;
  font-weight: 600;
  color: #000000;
  margin: 0 0 1.1rem;
`

export const ProgramBoxTitle = styled(BoxTitle)`
  font-size: 1.36rem;
  font-weight: 700;
  margin-bottom: 1.275rem;
`

export const ProgramToggle = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  width: 100%;
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
  text-align: left;

  & > ${ProgramBoxTitle} {
    margin: 0;
  }

  &[aria-expanded='true'] {
    margin-bottom: 1.275rem;
  }
`

export const ProgramChevron = styled.span`
  flex-shrink: 0;
  width: 0.65rem;
  height: 0.65rem;
  margin-right: 0.3rem;
  border-right: 2.5px solid #111111;
  border-bottom: 2.5px solid #111111;
  transform: translateY(${props => props.$open ? '25%' : '-25%'}) rotate(${props => props.$open ? '-135deg' : '45deg'});
  transition: transform 0.2s ease;
`
export const IncludeList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.7rem;
`

export const IncludeItem = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 0.6rem;
  font-family: Poppins;
  font-size: 0.8125rem;
  font-weight: 400;
  color: #000000;
  line-height: 1.45;
`

export const CheckMark = styled.span`
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #65E1FF;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: white;
  font-size: 0.5rem;
  font-weight: 700;
  margin-top: 1px;
  &::after { content: '✓'; }
`

export const DescBody = styled.p`
  font-family: Poppins;
  font-size: 0.8125rem;
  font-weight: 400;
  color: #000000;
  line-height: 1.65;
  margin: 0;
  white-space: pre-line;
`


export const ProgramGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.85rem;
  width: 90%;
  margin: 0 auto;
`

export const ProgramMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.34rem;
  margin-bottom: 0.425rem;
`

export const ProgramTag = styled.span`
  font-family: Poppins;
  font-size: 0.612rem;
  font-weight: 500;
  padding: 0.1275rem 0.51rem;
  border-radius: 999px;
  color: ${props => props.color || '#555555'};
  border: 1px solid ${props => props.color || '#D0D0D0'};
  background: white;
`

export const ModuleGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  grid-auto-rows: auto;
  align-content: center;
  gap: 0.95rem;
  width: 90%;
  margin: 0 15%;
`

export const ModuleCardWrapper = styled.div`
  filter: drop-shadow(0px 0px 5.24px rgba(43,123,199,0.25));
  width: 100%;
  min-width: 0;
  aspect-ratio: 256 / 288;
  outline: none;
  cursor: pointer;
  transition: filter 0.2s ease, transform 0.2s ease;

  &:hover, &:focus-visible {
    filter: drop-shadow(0px 0px 10px rgba(43,123,199,0.35));
    transform: translateY(-3px);
  }
`

export const ModuleCardPlaceholder = styled.div`
  width: 100%;
  aspect-ratio: 256 / 288;
  visibility: hidden;
  pointer-events: none;
`

export const ModulePopover = styled.div`
  position: fixed;
  z-index: 1000;
  width: min(620px, calc(100vw - 32px));
  background: white;
  border-radius: 18px;
  box-shadow: 0 16px 48px rgba(15, 23, 42, 0.22), 0 0 0 1px rgba(43, 123, 199, 0.08);
  padding: 1.5rem 1.75rem;
  display: flex;
  flex-direction: column;
  gap: 1.1rem;
`

export const ModalHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
`

export const ModalTitle = styled.h3`
  font-family: Poppins;
  font-size: 1.4rem;
  font-weight: 700;
  color: #000000;
  margin: 0;
  line-height: 1.25;
`

export const ModalDetailsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(170px, 1fr));
  gap: 0.75rem;
`

export const ModalDetailCard = styled.div`
  background: #F5F6FA;
  border-radius: 14px;
  padding: 1rem 1.1rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`

export const ModuleCard = styled.div`
  position: relative;
  background: white;
  clip-path: ${CARD_CLIP};
  overflow: hidden;
  display: flex;
  flex-direction: column;
  height: 100%;
  animation: ${moduleGridEnter} 300ms ease both;
  animation-delay: ${props => `${props.$index * 70}ms`};

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`

export const ModuleCardImage = styled.div`
  position: relative;
  height: 52.08%;
  flex-shrink: 0;
  overflow: hidden;
  background: #f0f4f8;
`

export const ModuleCardNumber = styled.div`
  position: absolute;
  z-index: 1;
  top: 13.6px;
  left: 13.6px;
  width: 40.8px;
  height: 40.8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1.7px;
  font-family: Poppins;
  font-weight: 700;
  color: #111111;

  &::before {
    content: '';
    position: absolute;
    z-index: 0;
    inset: 0;
    background: #FFF200;
    -webkit-mask-image: url(${numberCardIcon});
    mask-image: url(${numberCardIcon});
    -webkit-mask-repeat: no-repeat;
    mask-repeat: no-repeat;
    -webkit-mask-position: center;
    mask-position: center;
    -webkit-mask-size: contain;
    mask-size: contain;
  }

  & > span {
    position: relative;
    z-index: 1;
    font-size: 0.34rem;
    line-height: 1;
  }

  & > strong {
    position: relative;
    z-index: 1;
    font-size: 0.85rem;
    line-height: 1;
  }
`

export const ModuleCardImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`

export const ModuleCardBody = styled.div`
  padding: 0.6715rem 0.782rem 0.782rem;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
`

export const ModuleCardMeta = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.2975rem;
`

export const ModuleCardTag = styled.span`
  font-family: Poppins;
  font-size: 0.527rem;
  font-weight: 700;
  color: ${props => props.color || '#3494FA'};
  text-transform: uppercase;
  letter-spacing: 0.08em;
`

export const ModuleCardDuration = styled.span`
  font-family: Poppins;
  font-size: 0.5525rem;
  font-weight: 500;
  color: #919191;
  display: inline-flex;
  align-items: center;
  gap: 2.55px;
  text-transform: uppercase;
`

export const ModuleCardTitle = styled.h4`
  font-family: Poppins;
  font-size: 0.833rem;
  font-weight: 700;
  color: #000000;
  margin: 0 0 0.425rem;
  line-height: 1.25;
`

export const ModuleCardDesc = styled.p`
  font-family: Poppins;
  font-size: 0.6375rem;
  color: #6B7280;
  line-height: 1.6;
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`

export const ModuleCardTypes = styled.div`
  flex: 1;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  align-content: center;
  justify-content: center;
  gap: 0.255rem;
  padding: 0.34rem 0;

  & > span {
    font-size: 0.55rem;
    padding: 0.12rem 0.48rem;
  }
`

export const ModuleCardHint = styled.span`
  margin-top: auto;
  font-family: Poppins;
  font-size: 0.595rem;
  font-weight: 600;
  color: ${props => props.color || '#3494FA'};
  opacity: 0.6;
`

export const DetailList = styled.div`
  display: flex;
  flex-direction: column;
  background: #F5F6FA;
  border-radius: 10px;
  padding: 0.25rem 0.9rem;
`

export const DetailItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 0.75rem 0;
  border-bottom: 1px solid #E3E5EE;

  &:last-child {
    border-bottom: none;
  }
`

export const DetailNum = styled.div`
  min-width: 2.2rem;
  font-family: Poppins;
  font-size: 0.75rem;
  font-weight: 600;
  color: ${props => props.color || '#6462FC'};
  margin-top: 1px;
`

export const SequenceBlock = styled.div`
  box-sizing: border-box;
  width: 100%;
  background-color: #E9ECF5;
  background-image: url(${sequenceBackground});
  background-position: calc(50% - 4rem) bottom;
  background-size: 140% auto;
  background-repeat: no-repeat;
  border-radius: 13.6px;
  padding: 1.0625rem;
`

export const SequenceContent = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 2fr);
  align-items: start;
  gap: 2rem;
  width: 90%;
  margin: 1rem 1rem;

  @media (max-width: 900px) {
    grid-template-columns: minmax(0, 1fr);
    width: 100%;
  }
`

export const SequenceList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.6375rem;
  min-width: 0;
`

/* Shape cut on the top-left and bottom-right corners */
const chamfer = (c) => `polygon(
  ${c}px 0, 100% 0, 100% calc(100% - ${c}px), calc(100% - ${c}px) 100%, 0 100%, 0 ${c}px
)`

/* Same shape as an outline of thickness t: outer path + reversed inner path (hole) */
const chamferRing = (c, t) => {
  const k = c + t * 0.414
  return `polygon(
    0 ${c}px, ${c}px 0, 100% 0, 100% calc(100% - ${c}px), calc(100% - ${c}px) 100%, 0 100%, 0 ${c}px,
    ${t}px ${k}px, ${t}px calc(100% - ${t}px), calc(100% - ${k}px) calc(100% - ${t}px),
    calc(100% - ${t}px) calc(100% - ${k}px), calc(100% - ${t}px) ${t}px, ${k}px ${t}px, ${t}px ${k}px
  )`
}

const SEQ_CHAMFER = 12
const SEQ_RING_GAP = 6
const SEQ_RING_CHAMFER = SEQ_CHAMFER + SEQ_RING_GAP * 0.414 + 2

export const SequenceItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
`

export const SequenceHeader = styled.button`
  appearance: none;
  -webkit-appearance: none;
  position: relative;
  box-sizing: border-box;
  width: 100%;
  padding: ${SEQ_RING_GAP}px;
  border: none;
  background: none;
  color: inherit;
  cursor: pointer;
  font: inherit;
  text-align: left;

  /* Outer yellow outline of the active sequence */
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: #FFF200;
    clip-path: ${chamferRing(SEQ_RING_CHAMFER, 3)};
    opacity: ${props => props.active ? 1 : 0};
    transition: opacity 0.15s;
  }

  /* White corner mark, inside the outline's bottom-right cut */
  &::after {
    content: '';
    display: ${props => props.active ? 'block' : 'none'};
    position: absolute;
    right: 0;
    bottom: 0;
    width: 9px;
    height: 9px;
    background: #FFFFFF;
    clip-path: polygon(100% 0, 100% 100%, 0 100%);
  }

  &:focus-visible { outline: 2px solid #FFFFFF; outline-offset: 2px; }

  /* Hover only lightens unselected sequences; the selected one stays yellow */
  &[aria-pressed='false']:hover > span { background: #C3E4FF; }
  &[aria-pressed='true'] > span { background: #FFF200; }
`

export const SequenceBar = styled.span`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  min-height: 2.9rem;
  padding: 0.55rem 0.85rem;
  box-sizing: border-box;
  background: ${props => props.active ? '#FFF200' : '#A9D9FF'};
  clip-path: ${chamfer(SEQ_CHAMFER)};
  transition: background 0.15s;

`

const SEQ_NUMBER_ACCENTS = `
  linear-gradient(135deg, transparent 5px, #6B6B6B 5px, #6B6B6B 6.5px, transparent 6.5px) top left / 12px 12px no-repeat,
  linear-gradient(315deg, transparent 5px, #6B6B6B 5px, #6B6B6B 6.5px, transparent 6.5px) bottom right / 12px 12px no-repeat,
`

/* Black badge cut like the bar, with a thin grey accent along each cut when selected */
export const SequenceNumber = styled.span`
  flex-shrink: 0;
  width: 1.6rem;
  height: 1.6rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${props => props.active ? SEQ_NUMBER_ACCENTS : ''} #111111;
  clip-path: ${chamfer(5)};
  font-family: Poppins;
  font-size: 0.85rem;
  font-weight: 700;
  color: #FFFFFF;
`

export const SequenceTitle = styled.span`
  flex: 1;
  min-width: 0;
  font-family: Poppins;
  font-size: 0.75rem;
  font-weight: 700;
  line-height: 1.3;
  text-transform: uppercase;
  color: #111111;
`

export const SequenceDuration = styled.span`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-family: Poppins;
  font-size: 0.75rem;
  font-weight: 700;
  color: #111111;
  white-space: nowrap;

  & img {
    width: 12px;
    height: 12px;
    object-fit: contain;
    filter: brightness(0);
  }
`

export const SequenceDesc = styled.p`
  margin: 0;
  padding: 0 0.4rem;
  font-family: Poppins;
  font-size: 0.74375rem;
  font-weight: 400;
  line-height: 1.4;
  color: #FFFFFF;
  white-space: pre-line;
`

export const ProgramItem = styled.div`
  background: white;
  border-radius: 10.2px;
  box-shadow: ${SHADOW_CARD};
  padding: 0.85rem 1.0625rem;
  display: flex;
  align-items: flex-start;
  gap: 0.6375rem;
`

export const ProgramNum = styled.div`
  min-width: 19.55px;
  height: 19.55px;
  padding: 0 5.1px;
  box-sizing: border-box;
  border-radius: 999px;
  background: ${props => props.color || '#6462FC'};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-family: Poppins;
  font-size: 0.612rem;
  font-weight: 600;
  color: white;
  margin-top: 1px;
`

export const ProgramInfo = styled.div`
  flex: 1;
`

export const ProgramTitle = styled.div`
  font-family: Poppins;
  font-size: 0.74375rem;
  font-weight: 600;
  color: #000000;
  margin-bottom: 0.255rem;
`

export const ProgramDesc = styled.div`
  font-family: Poppins;
  font-size: 0.74375rem;
  font-weight: 400;
  color: #000000;
  line-height: 1.45;
  white-space: pre-line;
`

/* Header (2.875rem) + 6 rows (4rem + 1px border each), sticky header while scrolling */
export const AgencyTableScroll = styled.div`
  overflow: auto;
  max-height: calc(2.875rem + 6 * (4rem + 1px));

  th {
    position: sticky;
    top: 0;
    z-index: 1;
    /* border-collapse drops borders on sticky cells */
    box-shadow: inset 0 -2px 0 #D8DCE8;
  }
`
